<!-- TODO need optimization for clean group -->
<template>
  <div class="form-generator">
    <b-form ref="forms">
      <template v-if="withGroups">
        <div class="row row-group form-generator__items" v-for="(group, key, index) in fields.groups" :key="index">
          <div class="col col-12 form-generator__group-name">{{group.name}}</div>
          <FormGeneratorItems
            v-for="(field, key) in group.fields"
            :field="field"
            :key="key"
            v-model="field.value"
            @update="updateItem"
            @keyDown="keyDown"
          >
            <slot
              :name="field.slotName"
              :data="field"
              :fields="fields"
            >
            </slot>
            <template v-slot:errors v-if="$v.fields[key]">
              <template v-if="$v.fields[key].value.$error">
                <div
                  class="form-generator__error"
                  v-for="(param, index) in $v.fields[key].value.$params"
                  :key="index"
                >
                  <div v-show="!$v.fields[key].value[param.type]">
                    {{ showErrorByType(param) }}
                  </div>
                </div>
              </template>
            </template>
          </FormGeneratorItems>
        </div>
      </template>
      <div class="row form-generator__items" v-else>
        <FormGeneratorItems
          v-for="(field, key) in fields"
          v-show="!field.hidden"
          :field="field"
          :key="key"
          v-model="field.value"
          @update="updateItem"
          @keyDown="keyDown"
        >
          <slot
            :name="field.slotName"
            :data="field"
            :fields="fields"
          >
          </slot>
          <template v-slot:errors v-if="$v.fields[key]">
            <template v-if="$v.fields[key].value.$error">
              <div
                class="form-generator__error"
                v-for="(param, index) in $v.fields[key].value.$params"
                :key="index"
              >
                <div v-show="!$v.fields[key].value[param.type]">
                  {{ showErrorByType(param) }}
                </div>
              </div>
            </template>
          </template>
        </FormGeneratorItems>
      </div>
      <div class="row form-generator__items" v-if="showAddItems">
        <div class="row col-12 align-items-end" v-for="(item, key) in extraItems" :key="key">
          <FormGeneratorItems
            v-for="field in item"
            :field="field"
            :key="`${key}-${field.name}`"
            v-model="field.value"
            @update="updateItem"
          >
            <slot
              :name="field.slotName"
              :data="field"
              :fields="fields"
            >
            </slot>
            <template v-slot:errors v-if="$v.fields[key]">
              <template v-if="$v.fields[key].value.$error">
                <div
                  class="form-generator__error"
                  v-for="(param, index) in $v.fields[key].value.$params"
                  :key="index"
                >
                  <div v-show="!$v.fields[key].value[param.type]">
                    {{ showErrorByType(param) }}
                  </div>
                </div>
              </template>
            </template>
          </FormGeneratorItems>
          <b-button
            variant="secondary"
            class="button btn-icon ml-auto btn-item-delete"
            @click="deleteItem(key)"
          >
            <span class="material-icons">
              delete
            </span>
          </b-button>
        </div>
        <b-button @click.prevent="addItem" variant="info">
          Add {{ extraOptions.addBtn }}
        </b-button>
      </div>
      <div class="row form-generator__controls" v-if="showFormControls">
        <div class="col">
          <div class="form-generator-buttons">
            <slot name="form-footer-left-controllers"></slot>
            <b-button
              :disabled="isLoading"
              type="reset"
              variant="outline"
              class="button form-generator__button mr-2"
              @click.prevent="resetForm"
              v-if="showReset"
            >
              Reset
            </b-button>
            <b-button
              :disabled="isLoading"
              @click.prevent="submit"
              variant="primary"
              class="button form-generator__button"
            >
              {{textButtonSubmit}}
            </b-button>
            <slot name="form-footer-controllers"></slot>
          </div>
        </div>
        <slot name="footer-controllers"></slot>
      </div>
    </b-form>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { validationMixin } from 'vuelidate'
import { required, requiredIf, minLength } from 'vuelidate/lib/validators'
import FormGeneratorItems from '@components/FormGeneratorItems'

const errorText = {
  required: () => 'Field is required',
  requiredIf: () => 'Field is required',
  minLength: (param) => `Min Length ${param.min}`
}

const validateParam = {
  required: () => required,
  requiredIf: (c) => requiredIf(c),
  minLength: (n) => minLength(...n)
}

export default {
  name: 'FormGenerator',
  components: { FormGeneratorItems },
  mixins: [validationMixin],
  validations () {
    return {
      ...this.validationRules
    }
  },
  data () {
    return {
      fields: {},
      extraItems: []
    }
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    withGroups: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    showFormControls: {
      type: Boolean,
      default: true
    },
    showReset: {
      type: Boolean,
      default: true
    },
    textButtonSubmit: {
      type: String,
      default: 'Submit'
    },
    extraOptions: {
      type: Object,
      default: () => ({})
    },
    extraItem: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    'data': {
      deep: true,
      handler () {
        if (JSON.stringify(this.fields) !== JSON.stringify(this.data)) {
          this.setInitialForm()
        }
      }
    },
    'fields': {
      deep: true,
      handler () {
        this.$emit('input', this.fields)
      }
    }
  },
  created () {
    this.setInitialForm()
  },
  computed: {
    validationRules () {
      let result = { fields: {} }

      for (let key in this.data) {
        if (key === 'items' && ((this.fields.invoice_type && this.fields.invoice_type.value && this.fields.invoice_type.value.type !== 'hourly') ||
        (this.fields.payout_type && this.fields.payout_type.value && this.fields.payout_type.value.type !== 'hourly'))) {
          continue
        }
        if (key === 'periods' && this.fields.invoice_type.value && this.fields.invoice_type.value.type !== 'period') {
          continue
        }
        if (key === 'specialisations' && this.fields.invoice_type.value && this.fields.invoice_type.value.type !== 'specialisation') {
          continue
        }

        if (typeof this.data[key].skipValidation === 'function' && this.data[key].skipValidation(this.fields)) {
          continue
        }

        if (this.data[key].validation) {
          result.fields[key] = {
            value: {}
          }

          this.data[key].validation.map((validateItem) => {
            result.fields[key].value[validateItem.name] = validateParam[validateItem.name](validateItem.params)
          })
        }
      }

      return result
    },
    showAddItems () {
      if (!Object.keys(this.extraOptions).length) {
        return false
      }
      const triggerField = this.extraOptions.triggerField
      const triggerValue = this.extraOptions.triggerValue

      if (this.fields[triggerField].value && this.fields[triggerField].value.type === triggerValue) {
        return true
      }

      return false
    }
  },
  methods: {
    showErrorByType (param) {
      return errorText[param.type](param)
    },
    updateItem (itemName, fieldData) {
      this.$emit('updateItem', itemName, fieldData)
    },
    keyDown (event) {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.submit()
      }
    },
    submit () {
      this.validateForm()
      if (!this.$v.$invalid) {
        const fields = JSON.parse(JSON.stringify(this.fields))
        if (this.extraOptions.keySubmit/* && this.extraItems.length */) {
          fields[this.extraOptions.keySubmit] = this.extraItems
        }

        this.$emit('submit', fields)
      }
    },
    validateForm () {
      this.$v.$touch()
    },
    cleanField (fieldName, indexItem, group) {
      const field = group ? this.fields.groups[group].fields[fieldName] : this.fields[fieldName]
      const isArrayField = Array.isArray(field.value)
      let newFieldValue = isArrayField && field.type !== 'range_slider'
        ? field.value.filter((item, index) => index !== indexItem) : null

      if (group) {
        this.fields.groups[group].fields[fieldName] = {
          ...field,
          value: newFieldValue
        }
      } else {
        this.fields[fieldName] = {
          ...field,
          value: newFieldValue
        }
      }

      this.updateItem(fieldName, { ...field, value: newFieldValue })
    },
    cleanForm () {
      for (let key in this.fields) {
        const field = this.fields[key]
        this.fields[key].value = Array.isArray(field.value) && field.type !== 'range_slider' ? [] : null
      }

      this.$emit('clean', this.fields)
    },
    cleanFormWithGroups () {
      for (let groupKey in this.fields.groups) {
        for (let fieldKey in this.fields.groups[groupKey].fields) {
          const field = this.fields.groups[groupKey].fields[fieldKey]
          this.fields.groups[groupKey].fields[fieldKey].value = Array.isArray(field.value) && field.type !== 'range_slider' ? [] : null
        }
      }

      this.$emit('clean', this.fields)
    },
    resetForm () {
      this.fields = cloneDeep(this.data)
      this.$emit('reset', this.fields)
    },
    setInitialForm () {
      this.fields = cloneDeep(this.data)

      if (Object.keys(this.extraOptions).length &&
        this.extraOptions.keySubmit !== undefined &&
        this.fields[this.extraOptions.keySubmit] !== undefined) {
        const extraOptionFieldName = this.extraOptions.keySubmit

        for (const extraOptionItemKey in this.fields[extraOptionFieldName]) {
          const extraOptionItem = this.fields[extraOptionFieldName][extraOptionItemKey]
          const item = JSON.parse(JSON.stringify(this.extraItem))

          for (const itemFieldName in item) {
            let itemFieldItem = item[itemFieldName]
            if (itemFieldItem.type === 'select') {
              item[itemFieldName].value = itemFieldItem.options.find(itemFieldItemOption => itemFieldItemOption.id === extraOptionItem[itemFieldName])
            } else {
              item[itemFieldName].value = extraOptionItem[itemFieldName]
            }
          }
          this.extraItems.push(item)
        }
        delete this.fields[extraOptionFieldName]
      }
    },
    addItem () {
      const item = JSON.parse(JSON.stringify(this.extraItem))
      this.extraItems.push(item)
    },
    deleteItem (key) {
      this.extraItems.splice(key, 1)
    }
  }
}
</script>

<style lang="scss">
  .form-theme__primary{
    padding: 20px;
    background: $bgColor2;
    border-radius: 10px;
    margin-bottom: 30px;
    .form-generator-buttons{
      padding: 15px 0;
      margin-bottom: -15px;
      display: flex;
      justify-content: flex-end;
      border-top: 1px solid #CACAE1;
    }
  }
  .form-horizontal{
    &.form-generator{
      .form-generator__controls.row{
        margin-left: 10px;
      }
      form{
        display: flex;
        .row:not(.form-generator__controls) .col{
          min-width: 320px;
          padding-left: 10px;
          padding-right: 10px;
        }
      }
      .row{
        margin-left: -10px;
        margin-right: -10px;
      }
      .form-group{
        margin-bottom: 0;
        > div{
          display: flex;
        }
      }
      .form__label{
        margin-right: 10px;
        white-space: nowrap;
        margin-bottom: 0;
      }
    }
  }
  .btn-item-delete {
    margin-bottom: 15px;
  }
</style>
